import { InlineBar } from "@/components/shared/inline-bar";

/** 560 of 996 projects have no budget. "No budget set" is the honest rendering;
 *  a computed 0% variance would be an invented fact.
 *
 *  The sign alone did not carry the meaning: "+11.5%" in red and "-31.6%" in
 *  plain text left the reader to work out that positive means OVER. The word
 *  does the work now, and only the over case is coloured.
 *
 *  The word "budget" is in the text rather than left to the column header:
 *  the project page shows this figure beside a margin and an hours count, and
 *  a bare "31.6% under" in that company reads as under-anything.
 */
export function BudgetCell({
  variancePct,
  hasBudget,
  basis,
  barWidth = 96,
}: {
  variancePct: number | null;
  hasBudget: boolean;
  /**
   * Where the budget came from, in one sentence. Only the detail card passes
   * it -- in a grid the same sentence would repeat down every row. Both places
   * take the wording from BUDGET_BASIS so the page cannot describe one number
   * two different ways.
   */
  basis?: string;
  /** Burn bar width in px: 96 in list cells, 120 in the detail card. */
  barWidth?: number;
}) {
  if (!hasBudget || variancePct === null) {
    return <span className="whitespace-nowrap text-muted-foreground">No budget set</span>;
  }
  const over = variancePct > 0;
  // variance is "over by x%"; burn is "used x% of budget". +31.2% over is 1.312.
  const burn = 1 + variancePct / 100;
  const words = (
    <span
      className={`whitespace-nowrap tabular-nums ${over ? "text-destructive" : "text-foreground"}`}
    >
      {Math.abs(variancePct).toFixed(1)}% {over ? "over" : "under"} budget
    </span>
  );
  const bar = <InlineBar value={burn} mode="burn" width={barWidth} />;
  // In a grid the bar sits UNDER the words so the column stays as wide as the
  // words alone: beside them it pushed the eight-column projects table into a
  // horizontal scroll at 1440. The detail card has the room, so there the bar
  // sits beside the verdict with the basis line beneath.
  if (!basis) {
    return (
      <span className="flex flex-col items-start gap-1">
        {words}
        {bar}
      </span>
    );
  }
  return (
    <span className="block">
      <span className="inline-flex items-center gap-2">
        {bar}
        {words}
      </span>
      <span className="mt-1 block text-xs font-normal text-muted-foreground">{basis}</span>
    </span>
  );
}
